import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, Loader2, Minus, Plus, X } from 'lucide-react';
import { Button } from '@/components/ui';
import { usePatchUserClubBenefit } from '@/services';
import { cn } from '@/lib/utils';

function parseUsedCountDraft(raw: string): number | null {
	const trimmed = raw.trim();
	if (!/^-?\d+$/.test(trimmed)) return null;
	const parsed = Number.parseInt(trimmed, 10);
	return Number.isFinite(parsed) ? parsed : null;
}

const USED_COUNT_MIN = -99;
const USED_COUNT_MAX = 99;

function validateUsedCountForUi(value: number): string | null {
	if (value < USED_COUNT_MIN || value > USED_COUNT_MAX) {
		return `Informe um valor entre ${USED_COUNT_MIN} e ${USED_COUNT_MAX}.`;
	}
	return null;
}

function formatBenefitRatio(value: number, defaultAmount: number): string {
	return `${value}/${defaultAmount}`;
}

interface EditUserBenefitUsageModalProps {
	open: boolean;
	onClose: () => void;
	userId: string;
	row: Clubs.UserBenefitBalanceItem;
	benefitName: string;
}

const EditUserBenefitUsageModal = ({
	open,
	onClose,
	userId,
	row,
	benefitName,
}: EditUserBenefitUsageModalProps) => {
	const [draft, setDraft] = useState(String(row.used_count));
	const [confirming, setConfirming] = useState(false);

	const { mutate: patchBenefit, isPending } = usePatchUserClubBenefit(() => {
		onClose();
	});

	useEffect(() => {
		if (open) {
			setDraft(String(row.used_count));
			setConfirming(false);
		}
	}, [open, row.used_count]);

	const draftNum = parseUsedCountDraft(draft);
	const effectiveCount = draftNum ?? row.used_count;
	const validationError =
		draftNum !== null
			? validateUsedCountForUi(draftNum)
			: draft.trim() !== ''
			  ? 'Informe um número inteiro válido.'
			  : 'Informe o uso desejado.';

	const isDirty = draftNum !== null && draftNum !== row.used_count;
	const canSave = isDirty && !validationError;

	const displayedUsage =
		draftNum !== null && !validationError ? draftNum : row.used_count;

	const adjustDraft = (delta: number) => {
		const current = draftNum ?? row.used_count;
		const next = Math.min(
			USED_COUNT_MAX,
			Math.max(USED_COUNT_MIN, current + delta),
		);
		setDraft(String(next));
	};

	const handleSaveClick = () => {
		if (!canSave || draftNum === null) return;
		setConfirming(true);
	};

	const handleConfirm = () => {
		if (!canSave || draftNum === null) return;
		patchBenefit({
			userId,
			body: {
				club_benefit_id: row.club_benefit_id,
				used_count: draftNum,
			},
		});
	};

	if (!open) return null;

	return createPortal(
		<div className="fixed inset-0 z-[10020] flex items-center justify-center p-4">
			<div
				className="absolute inset-0 bg-stone-900/55 backdrop-blur-[2px]"
				onClick={isPending ? undefined : onClose}
				aria-hidden
			/>
			<div
				className="relative w-full max-w-xl rounded-3xl border border-zinc-200 bg-white p-6 shadow-2xl"
				role="dialog"
				aria-modal="true"
				aria-labelledby="edit-benefit-usage-title"
				onClick={(e) => e.stopPropagation()}
			>
				<button
					type="button"
					onClick={onClose}
					disabled={isPending}
					className="absolute right-4 top-4 rounded-lg p-1.5 text-zinc-400 transition-colors hover:bg-zinc-100 hover:text-zinc-700 disabled:opacity-50"
					aria-label="Fechar"
				>
					<X className="size-5" />
				</button>

				{confirming ? (
					<div className="space-y-5 pt-1">
						<div className="flex justify-center">
							<div className="rounded-full bg-amber-50 p-3 text-amber-700">
								<AlertTriangle className="size-7" aria-hidden />
							</div>
						</div>
						<div className="space-y-2 text-center">
							<h3
								id="edit-benefit-usage-title"
								className="text-lg font-bold text-zinc-900"
							>
								Confirmar alteração
							</h3>
							<p className="text-sm leading-relaxed text-zinc-600">
								Alterar o uso de{' '}
								<span className="font-semibold text-zinc-900">
									{benefitName}
								</span>{' '}
								de{' '}
								<span className="font-mono font-semibold text-zinc-900">
									{formatBenefitRatio(
										row.used_count,
										row.default_amount,
									)}
								</span>{' '}
								para{' '}
								<span className="font-mono font-semibold text-amber-800">
									{formatBenefitRatio(
										draftNum ?? row.used_count,
										row.default_amount,
									)}
								</span>
								?
							</p>
							<p className="text-xs text-zinc-500">
								O valor substitui o uso atual e será registrado
								na linha do tempo como alteração manual.
							</p>
						</div>
						<div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
							<Button
								type="button"
								variant="outline"
								text="Voltar"
								className="h-10 rounded-xl border-zinc-200 px-4 text-xs font-bold"
								disabled={isPending}
								onClick={() => setConfirming(false)}
							/>
							<Button
								type="button"
								variant="contained"
								text={
									isPending
										? 'Salvando…'
										: 'Confirmar alteração'
								}
								className="h-10 rounded-xl bg-amber-800 px-4 text-xs font-bold text-white hover:bg-amber-900 disabled:opacity-60"
								startIcon={
									isPending ? (
										<Loader2 className="size-3.5 animate-spin" />
									) : undefined
								}
								disabled={isPending}
								onClick={handleConfirm}
							/>
						</div>
					</div>
				) : (
					<div className="space-y-5 pt-1">
						<div className="pr-8">
							<h3
								id="edit-benefit-usage-title"
								className="text-lg font-bold text-zinc-900"
							>
								Alterar uso do benefício
							</h3>
							<p className="mt-1 text-sm font-medium text-zinc-700">
								{benefitName}
							</p>
						</div>

						<div className="flex gap-3 rounded-2xl border border-amber-100 bg-amber-50/90 p-4">
							<AlertTriangle
								className="mt-0.5 size-4 shrink-0 text-amber-700"
								aria-hidden
							/>
							<p className="text-xs leading-relaxed text-amber-900">
								Ação sensível: o valor informado{' '}
								<strong>substitui</strong> o uso atual do
								paciente (permite negativos para reset manual).
								Confirme antes de salvar.
							</p>
						</div>

						<div className="rounded-2xl border border-zinc-100 bg-zinc-50/80 p-4 text-center">
							<p className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">
								Uso / valor padrão
							</p>
							<p className="mt-1 text-lg font-bold tabular-nums text-zinc-900">
								{formatBenefitRatio(
									displayedUsage,
									row.default_amount,
								)}
							</p>
						</div>

						<div>
							<label
								htmlFor="benefit-used-count-input"
								className="mb-2 block text-[10px] font-bold uppercase tracking-wider text-zinc-500"
							>
								Novo uso
							</label>
							<div className="flex items-center justify-center gap-2">
								<button
									type="button"
									onClick={() => adjustDraft(-1)}
									disabled={
										isPending ||
										effectiveCount <= USED_COUNT_MIN
									}
									className="flex size-10 items-center justify-center rounded-xl border border-zinc-200 bg-white text-zinc-700 transition-colors hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-40"
									aria-label="Diminuir em 1"
								>
									<Minus className="size-4 stroke-[2.5]" />
								</button>
								<input
									id="benefit-used-count-input"
									type="number"
									min={USED_COUNT_MIN}
									max={USED_COUNT_MAX}
									step={1}
									value={draft}
									onChange={(e) => setDraft(e.target.value)}
									className={cn(
										'h-12 w-24 rounded-xl border bg-white px-2 text-center text-lg font-bold tabular-nums text-zinc-900 focus:outline-none focus:ring-2',
										validationError && draft.trim() !== ''
											? 'border-rose-300 focus:border-rose-400 focus:ring-rose-100'
											: 'border-zinc-200 focus:border-zinc-400 focus:ring-zinc-100',
									)}
									aria-invalid={!!validationError}
								/>
								<button
									type="button"
									onClick={() => adjustDraft(1)}
									disabled={
										isPending ||
										effectiveCount >= USED_COUNT_MAX
									}
									className="flex size-10 items-center justify-center rounded-xl border border-zinc-200 bg-white text-zinc-700 transition-colors hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-40"
									aria-label="Aumentar em 1"
								>
									<Plus className="size-4 stroke-[2.5]" />
								</button>
							</div>
							{validationError ? (
								<p className="mt-2 text-center text-xs text-rose-600">
									{validationError}
								</p>
							) : null}
						</div>

						<div className="flex flex-col-reverse gap-2 border-t border-zinc-100 pt-4 sm:flex-row sm:justify-end">
							<Button
								type="button"
								variant="outline"
								text="Fechar"
								className="h-10 rounded-xl border-zinc-200 px-4 text-xs font-bold"
								disabled={isPending}
								onClick={onClose}
							/>
							<Button
								type="button"
								variant="contained"
								text="Salvar"
								className="h-10 rounded-xl bg-zinc-900 px-4 text-xs font-bold text-white hover:bg-black disabled:opacity-50"
								disabled={!canSave || isPending}
								onClick={handleSaveClick}
							/>
						</div>
					</div>
				)}
			</div>
		</div>,
		document.body,
	);
};

export { EditUserBenefitUsageModal };
